import { z } from "zod";
import { EVENT_TYPE, OUTCOME, POLICY_SOURCE, REASON_CODE, } from "../../../constants/eventLog.js";
import { CANCELLATION_INITIATOR } from "../../../constants/workflow.js";
import { decimalString } from "../../common.js";
import { eventLogBaseSchema } from "../base.js";
/**
 * Billing events: the cancellation and did-not-attend policies.
 *
 * Every schema here is `.strict()`. An unrecognised key is a bug - a typo'd field name is
 * silently unreadable forever once written, and an audit row cannot be corrected after the
 * fact. Failing at the call site is the only point at which it is still fixable.
 */
/**
 * Which config produced the policy that was applied, and what it said.
 *
 * `source` matters because company settings overwrite the global ones in a local variable
 * inside ChargeService - once that has run there is otherwise no way to tell which set won.
 */
export const policySchema = z
    .object({
    source: z.enum(POLICY_SOURCE),
    /** Hours. Absent on DNA events, which have no penalty period. */
    penaltyPeriodHours: z.number().nullable().optional(),
    penaltyPercentage: z.number().nullable().optional(),
    penaltyCap: decimalString.nullable().optional(),
})
    .strict();
/**
 * The inputs the policy decision was computed from.
 *
 * Recorded because the decision is time-sensitive: `minutesUntilAppointment` cannot be
 * recomputed later, since it depends on when the cancellation happened rather than on any
 * value still stored on the booking.
 */
export const evaluationSchema = z
    .object({
    minutesUntilAppointment: z.number().nullable().optional(),
    penaltyPeriodMinutes: z.number().nullable().optional(),
    capTriggered: z.boolean().optional(),
    /** Total before the cap was applied. Equal to the applied total when uncapped. */
    uncappedTotal: decimalString.optional(),
    appliedTotal: decimalString.optional(),
})
    .strict();
/**
 * The booking's date/time fields as they stood when no penalty period could be evaluated.
 *
 * Kept separate from `evaluationSchema` because these are the *absent* inputs - the reason
 * the evaluation could not run, rather than its result.
 */
export const missingDateTimeSchema = z
    .object({
    date: z.union([z.string(), z.date()]).nullable(),
    startHours: z.number().nullable(),
    startMinutes: z.number().nullable(),
})
    .strict();
/**
 * Context carried by every cancellation/DNA event regardless of outcome.
 *
 * `cancellationInitiator` is deliberately here and not folded into `actorType`: the actor is
 * whoever clicked the button (a manager, always), while the initiator is on whose behalf it
 * happened. A practice-initiated cancellation is still performed by a manager, and
 * collapsing the two loses exactly the distinction that decides whether anyone is charged.
 */
const cancellationContextSchema = z.object({
    cancellationInitiator: z.enum(CANCELLATION_INITIATOR).nullable(),
    cancellationComment: z.string().nullable(),
    /** When the respondent asked (ISO 8601 with timezone). The penalty period is judged from this. */
    cancellationRequestedAt: z.string().nullable().optional(),
});
/** A penalty was charged: cancellation inside the penalty period, or did-not-attend. */
const policyAppliedMetadataSchema = cancellationContextSchema
    .extend({
    policy: policySchema,
    evaluation: evaluationSchema,
})
    .strict();
/**
 * No penalty was charged. Which inputs are present depends on how far the decision got
 * before it bailed, so both are optional - a booking with no date/time never reached the
 * point of resolving a policy at all.
 */
const policySkippedMetadataSchema = cancellationContextSchema
    .extend({
    policy: policySchema.optional(),
    evaluation: evaluationSchema.optional(),
    missingDateTime: missingDateTimeSchema.optional(),
})
    .strict();
/**
 * A booking left a cancellation status and its original charges were put back. Carries no
 * policy: nothing was evaluated, the earlier penalty was simply undone.
 */
const policyRevertedMetadataSchema = z.object({}).strict();
/**
 * Every billing event, in the form the top-level union expects.
 *
 * Exported as an array so `../index.ts` can concatenate it with other features' arrays
 * without naming each member. Adding an event here needs no change to the union.
 */
export const billingEventSchemas = [
    eventLogBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BILLING_CANCELLATION_POLICY_APPLIED),
        outcome: z.literal(OUTCOME.APPLIED),
        reasonCode: z.null().optional(),
        metadata: policyAppliedMetadataSchema,
    })
        .strict(),
    eventLogBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BILLING_DNA_POLICY_APPLIED),
        outcome: z.literal(OUTCOME.APPLIED),
        reasonCode: z.null().optional(),
        metadata: policyAppliedMetadataSchema,
    })
        .strict(),
    eventLogBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BILLING_CANCELLATION_POLICY_SKIPPED),
        outcome: z.literal(OUTCOME.SKIPPED),
        /**
         * Required here, unlike on the other events. "Why was this booking not charged" is
         * the question a dispute actually asks, and `outcome: SKIPPED` alone does not answer
         * it - several different branches produce it.
         */
        reasonCode: z.enum(REASON_CODE),
        metadata: policySkippedMetadataSchema,
    })
        .strict(),
    eventLogBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BILLING_CANCELLATION_POLICY_REVERTED),
        outcome: z.literal(OUTCOME.REVERTED),
        reasonCode: z.null().optional(),
        metadata: policyRevertedMetadataSchema,
    })
        .strict(),
];
//# sourceMappingURL=billing.js.map